import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiInstance from '@/utils/api'
import { setLanguage } from '@/i18n'
import { resetAllStores } from '@/utils/storeReset'
import { isDatevAccountant as isDatevAccountantUser } from '@/utils/authorization'
import { updateElementPlusLocale } from '@/utils/elementPlusLocale'


export interface Permission {
  id: number
  name: string
  guard_name: string
  created_at: string
  updated_at: string
  pivot: {
    model_type?: string
    model_id?: number
    permission_id?: number
  }
}

export interface Role {
  id: number
  name: string
  guard_name: string
  created_at: string
  updated_at: string
  pivot: {
    model_type: string
    model_id: number
    role_id: number
  }
  permissions: Permission[]
}

export interface User {
  id: string | number
  name: string
  email: string
  role: 'super_admin' | 'partner' | 'restaurant_company' | 'pickup_monitor' | 'reception' | 'employee' | 'user'
  companyId?: string
  companyType?: 'partner' | 'restaurant_company'
  restaurantId?: string
  item_modal_view?: number
  roleId?: number
  currencyCode?: string
  countryId?: number | null
  firstName?: string
  lastName?: string
  company?: string
  companyName?: string
  street?: string
  zipCode?: string
  city?: string
  vatNumber?: string | null
  mobileNo?: string
  defaultLang?: string
  active?: number
  roles?: Role[]
  permissions?: Permission[]
  slug?: string
  logoUrl?: string | null
  logoMediaId?: number | null
  mediaUrl?: string | null
  rest_pin_number?: boolean
  stripeCustomerId?: string | null
  partnerName?: string
  isPrepaid?: boolean
}

export interface Impersonator {
  id: number | string
  name: string
  email: string
}

// localStorage key holding the Super Admin's own token while impersonating.
const ADMIN_TOKEN_KEY = 'adminAuthToken'
// localStorage key holding the impersonation target type ('user' | 'company' | 'partner').
const IMPERSONATION_TYPE_KEY = 'impersonationTargetType'

// Reception accounts get their own session keys so an admin session and a reception
// session can be logged in independently in the same browser (different tabs, or a
// tab + the installed reception PWA) without one overwriting the other's localStorage.
const RCP_TOKEN_KEY = 'rcpAuthToken'
const RCP_AUTH_FLAG_KEY = 'rcpIsAuthenticated'
const RCP_USER_KEY = 'rcpUser'
// Set while the reception session is the one signup opened (see markReceptionSessionFromSignup).
const RCP_FROM_SIGNUP_KEY = 'rcpSessionFromSignup'

// Reception routes are role-gated to 'reception' already, so before a profile has
// loaded, the current path is a reliable proxy for "which session should this be".
function isRcpPath(path: string = window.location.pathname) {
  return path.startsWith('/rcp/')
}

function keysFor(role?: string) {
  const rcp = role === 'reception'
  return {
    token: rcp ? RCP_TOKEN_KEY : 'authToken',
    authFlag: rcp ? RCP_AUTH_FLAG_KEY : 'isAuthenticated',
    user: rcp ? RCP_USER_KEY : 'user',
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  // Initial synchronous read must already be zone-scoped (admin vs reception) — this
  // runs before initializeAuth()'s async profile fetch resolves, and App.vue's
  // post-mount auth check reads isAuthenticated.value immediately, not the eventual
  // fetchUserProfile result. Getting the wrong key here means a valid reception (or
  // admin) session reads as logged-out and gets redirected to /login on every refresh.
  const token = ref<string | null>(localStorage.getItem(isRcpPath() ? RCP_TOKEN_KEY : 'authToken'))
  const isAuthenticated = ref(localStorage.getItem(isRcpPath() ? RCP_AUTH_FLAG_KEY : 'isAuthenticated') === 'true')

  // Impersonation state. Hydrated from the /user/profile response so it survives reloads.
  const isImpersonating = ref(false)
  const impersonator = ref<Impersonator | null>(null)

  const currentUser = computed(() => user.value)

  // Stored as an Employee, told apart by role name — see utils/authorization.
  const isDatevAccountant = computed(() => isDatevAccountantUser(user.value))

  const isManagementRole = computed(() =>
    ['super_admin', 'partner', 'restaurant_company', 'employee'].includes(user.value?.role ?? '')
  )

  const login = (userData: User) => {
    user.value = userData
    isAuthenticated.value = true
    const keys = keysFor(userData.role)
    localStorage.setItem(keys.authFlag, 'true')
    localStorage.setItem(keys.user, JSON.stringify(userData))
  }

  const fetchUserProfile = async (authToken: string) => {
    try {
      const response = await apiInstance.get('/user/profile', {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      })

      const data = response.data

      if (data.success === false || !data.data || !data.data.user) {
        return {
          success: false,
          unauthorized: false,
          userData: null,
          message: 'Failed to fetch user profile',
        }
      }

      const apiUser = data.data.user
      // Reception alarm tone comes from the account's uploaded audio in `media`. Pick the
      // audio item explicitly (by mime type) so it stays correct even if the media list
      // holds more than one entry or a non-audio item comes first; fall back to the first.
      const audioMedia = Array.isArray(apiUser.media)
        ? (apiUser.media.find((m: any) => typeof m?.mime_type === 'string' && m.mime_type.startsWith('audio/')) ?? apiUser.media[0])
        : null
      const userData: User = {
        id: apiUser.id,
        name: `${apiUser.first_name} ${apiUser.last_name}`.trim(),
        email: apiUser.email,
        role: mapRoleIdToRole(apiUser.role_id),
        roleId: apiUser.role_id,
        companyId: apiUser.company_id ? String(apiUser.company_id) : undefined,
        companyType: apiUser.company_type ?? undefined,
        restaurantId: apiUser.restaurant_id,
        item_modal_view: apiUser.item_modal_view,
        currencyCode: apiUser.currency_code,
        countryId: apiUser.country_id ?? null,
        firstName: apiUser.first_name,
        lastName: apiUser.last_name,
        company: apiUser.company,
        companyName: apiUser.company_name,
        street: apiUser.street,
        zipCode: apiUser.zip_code,
        city: apiUser.city,
        vatNumber: apiUser.vat_number ?? null,
        mobileNo: apiUser.mobile_no,
        defaultLang: apiUser.default_lang,
        active: apiUser.active,
        roles: apiUser.roles || [],
        permissions: apiUser.permissions || [],
        slug: apiUser.slug ?? undefined,
        logoUrl: apiUser.logo_url ?? null,
        logoMediaId: apiUser.logo_media_id ?? null,
        mediaUrl: audioMedia?.original_url ?? null,
        rest_pin_number: apiUser.rest_pin_number,
        stripeCustomerId: apiUser.stripe_customer_id ?? null,
        partnerName: apiUser.partner_name ?? null,
        isPrepaid: apiUser.is_prepaid ?? false,
      }

      // Keep impersonation state in sync with the server on every profile fetch
      // (login, reload, take/leave) so the banner is always accurate.
      isImpersonating.value = apiUser.is_impersonating === true
      impersonator.value = apiUser.impersonator ?? null

      return {
        success: true,
        unauthorized: false,
        userData,
        message: null,
      }
    } catch (error: any) {
      // HTTP status code from the response status line (undefined for a network/CORS/
      // timeout error, which produces no response at all).
      const httpStatus = error?.response?.status
      console.error('Profile fetch error:', httpStatus, error.message)
      return {
        success: false,
        // End the session ONLY on a genuine auth rejection — HTTP 401. The backend
        // returns 401 ("Unauthenticated.") for a missing/invalid/expired token (Sanctum
        // default), 403 for a permission/role failure, and no response for a network
        // error. So 401 is the single reliable signal that the token itself is dead.
        unauthorized: httpStatus === 401,
        userData: null,
        message: 'Network error while fetching profile',
      }
    }
  }

  const getCookie = (name: string) => {
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find(row => row.startsWith(name + '='));
    return cookie ? decodeURIComponent(cookie.split('=')[1]) : '';
  }

  const loginWithAPI = async (email: string, password: string, rememberMe = false, lang: string = 'de') => {
    try {
      const trk_dv = getCookie('trk_dv');
      const response = await apiInstance.post('/auth/login', { email, password, remember_me: rememberMe, trk_dv: trk_dv, lang})
      const data = response.data

      if (data.error === true) {
        return {
          success: false,
          requires_otp: false,
          otp_id: null as number | null,
          email: null as string | null,
          errors: data.errors || null,
          message: data.message || null,
        }
      }

      if (!data.data) {
        return {
          success: false,
          requires_otp: false,
          otp_id: null as number | null,
          email: null as string | null,
          errors: null,
          message: 'Invalid response from server',
        }
      }

      // 2FA required: backend returns otp_id instead of token
      if (data.data.otp_id && !data.data.token) {
        return {
          success: false,
          requires_otp: true,
          otp_id: data.data.otp_id as number,
          email: data.data.email as string || email,
          errors: null,
          message: null,
        }
      }

      if (!data.data.token) {
        return {
          success: false,
          requires_otp: false,
          otp_id: null as number | null,
          email: null as string | null,
          errors: null,
          message: 'Invalid response from server',
        }
      }

      const authToken = data.data.token
      token.value = authToken

      const profileResult = await fetchUserProfile(authToken)
      if (profileResult.success && profileResult.userData) {
        localStorage.setItem(keysFor(profileResult.userData.role).token, authToken)
        login(profileResult.userData)
        if (profileResult.userData.defaultLang) {
          setLanguage(profileResult.userData.defaultLang)
          updateElementPlusLocale(profileResult.userData.defaultLang)
        }
        return {
          success: true,
          requires_otp: false,
          otp_id: null as number | null,
          email: null as string | null,
          errors: null,
          message: null,
        }
      } else {
        logout()
        return {
          success: false,
          requires_otp: false,
          otp_id: null as number | null,
          email: null as string | null,
          errors: null,
          message: profileResult.message || 'Failed to fetch user profile',
        }
      }
    } catch (error: any) {
      if (error.response?.status === 422 && error.response?.data?.errors) {
        return {
          success: false,
          requires_otp: false,
          otp_id: null as number | null,
          email: null as string | null,
          errors: error.response.data.errors,
          message: null,
        }
      }

      return {
        success: false,
        requires_otp: false,
        otp_id: null as number | null,
        email: null as string | null,
        errors: null,
        message: error.response?.data?.message || 'Network error. Please try again.',
      }
    }
  }

  const verifyOTP = async (otpId: number, code: string | number, rememberMe = false, email = '', lang: string = 'de') => {
    try {
      const response = await apiInstance.post('/auth/verify-otp', {
        otp_id: otpId,
        code: code,
        remember_me: rememberMe,
        lang
      })
      const data = response.data

      if (data.error === true) {
        return {
          success: false,
          errors: data.errors || null,
          message: data.message || null,
        }
      }

      if (!data.data || !data.data.token) {
        return {
          success: false,
          errors: null,
          message: 'Invalid response from server',
        }
      }

      const authToken = data.data.token
      token.value = authToken

      if (data.data.trusted && email) {
        localStorage.setItem('trusted_device_email', email)
      }

      if(data.data.trk_dv){

        let code = data.data.trk_dv;

        if (code.name && code.value != null) {
          const expiresDate = new Date(code.expires * 1000).toUTCString();

          let cookieString = `${code.name}=${encodeURIComponent(code.value)}`;

          // Expires
          if (code.expires) {
            cookieString += `; expires=${expiresDate}`;
          }

          // Path
          if (code.path) {
            cookieString += `; path=${code.path}`;
          }

          // Secure
          if (code.secure) {
            cookieString += `; Secure`;
          }

          // SameSite
          if (code.sameSite) {
            cookieString += `; SameSite=${code.sameSite}`;
          }

          document.cookie = cookieString;
        }
      }

      const profileResult = await fetchUserProfile(authToken)
      if (profileResult.success && profileResult.userData) {
        localStorage.setItem(keysFor(profileResult.userData.role).token, authToken)
        login(profileResult.userData)
        if (profileResult.userData.defaultLang) {
          setLanguage(profileResult.userData.defaultLang)
          updateElementPlusLocale(profileResult.userData.defaultLang)
        }
        return {
          success: true,
          errors: null,
          message: null,
        }
      } else {
        logout()
        return {
          success: false,
          errors: null,
          message: profileResult.message || 'Failed to fetch user profile',
        }
      }
    } catch (error: any) {
      console.error('OTP verify error:', error.response?.status, error.message)

      if (error.response?.status === 422 && error.response?.data?.errors) {
        return {
          success: false,
          errors: error.response.data.errors,
          message: null,
        }
      }

      return {
        success: false,
        errors: null,
        message: error.response?.data?.message || 'Network error. Please try again.',
      }
    }
  }

  const resendOTP = async (otpId: number, lang: string = 'de') => {
    try {
      const response = await apiInstance.post('/auth/resend-otp', { otp_id: otpId, lang })
      const data = response.data

      if (data.error === true) {
        return {
          success: false,
          otp_id: null as number | null,
          message: data.message || null,
          lang
        }
      }

      return {
        success: true,
        otp_id: (data.data?.otp_id as number) || null,
        message: data.message || null,
      }
    } catch (error: any) {
      console.error('OTP resend error:', error.response?.status, error.message)
      return {
        success: false,
        otp_id: null as number | null,
        message: error.response?.data?.message || 'Network error. Please try again.',
        retry_after_seconds: (error.response?.data?.retry_after_seconds as number) ?? null,
      }
    }
  }

  /**
   * Log in with the one-time link emailed to a Datev Accountant. The link is the
   * credential, so there is no OTP step: the API swaps it for a session token.
   */
  const loginWithMagicToken = async (magicToken: string) => {
    try {
      const response = await apiInstance.post('/auth/magic-login', { token: magicToken })
      const data = response.data

      if (data.error === true || !data.data?.token) {
        return { success: false, message: data.message || null }
      }

      const authToken = data.data.token

      // Someone may already be signed in on this browser. Their session is only
      // replaced once the new one is proven, so a failure leaves it untouched.
      const profileResult = await fetchUserProfile(authToken)
      if (!profileResult.success || !profileResult.userData) {
        return { success: false, message: profileResult.message || null }
      }

      resetAllStores()
      token.value = authToken
      localStorage.setItem(keysFor(profileResult.userData.role).token, authToken)
      login(profileResult.userData)
      if (profileResult.userData.defaultLang) {
        setLanguage(profileResult.userData.defaultLang)
        updateElementPlusLocale(profileResult.userData.defaultLang)
      }

      return { success: true, message: null }
    } catch (error: any) {
      // The API answers every bad link the same way; a network failure is the only other case.
      return { success: false, message: error.response?.data?.message || null }
    }
  }

  const mapRoleIdToRole = (roleId: number): User['role'] => {
    const roleMap: Record<number, User['role']> = {
      1: 'restaurant_company',
      4: 'super_admin',
      5: 'employee',
      7: 'reception',
      8: 'partner',
      9: 'pickup_monitor',
    }
    return roleMap[roleId] || 'user'
  }

  const forgotPassword = async (email: string, lang: string = 'de') => {
    try {
      const response = await apiInstance.post('/forgot-password', { email, lang })
      const data = response.data

      if (data.error === true) {
        return {
          success: false,
          errors: data.errors || null,
          message: data.message || null,
          lang
        }
      }

      if (response.status === 200 && data.error === false) {
        return {
          success: true,
          errors: null,
          message: data.message || 'Password reset email has been sent. Please check your email inbox and follow the instructions to reset your password.',
        }
      }

      return {
        success: false,
        errors: null,
        message: 'An unexpected error occurred',
      }
    } catch (error: any) {
      console.error('Forgot password error:', error.response?.status, error.message)

      if (error.response?.status === 422 && error.response?.data?.errors) {
        return {
          success: false,
          errors: error.response.data.errors,
          message: null,
        }
      }

      return {
        success: false,
        errors: null,
        message: error.response?.data?.message || 'Network error. Please try again.',
      }
    }
  }

  // Signup logs the new reception account in for its terminal preview. That session
  // belongs to the owner's signup, so it is marked and ended with their company logout;
  // a reception session logged in any other way is left running.
  const markReceptionSessionFromSignup = () => {
    localStorage.setItem(RCP_FROM_SIGNUP_KEY, 'true')
  }

  // A reception login done by hand (login form, in-app QR scan) sets up a real terminal
  // on this device, which a company logout must not end. The signup login link
  // (rcp/TokenLogin) keeps the mark: it continues the session signup opened.
  const clearReceptionSignupMark = () => {
    localStorage.removeItem(RCP_FROM_SIGNUP_KEY)
  }

  // Tokens are read from storage, not token.value: after signup the in-memory store
  // can hold the other session. Storage is cleared before the API calls, so a 401 on
  // an expired token can't make the api.ts interceptor restore a stashed
  // impersonation admin token. The calls are not awaited: the session is already
  // gone locally, so the caller can leave the page right away.
  const logoutWithAPI = async () => {
    const contexts: Array<'admin' | 'reception'> = isRcpPath()
      ? ['reception']
      : localStorage.getItem(RCP_FROM_SIGNUP_KEY) === 'true'
        ? ['admin', 'reception']
        : ['admin']
    const authTokens = contexts
      .map((context) => localStorage.getItem(context === 'reception' ? RCP_TOKEN_KEY : 'authToken'))
      .filter((authToken): authToken is string => !!authToken)

    contexts.forEach((context) => logout(context))

    authTokens.forEach((authToken) => {
      apiInstance.post('/auth/logout', {}, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      }).catch((error: any) => {
        console.error('Logout error:', error.response?.status, error.message)
      })
    })

    return true
  }

  // context omitted -> inferred from the current path (isRcpPath()). Pass it explicitly
  // whenever the caller already knows which session this is (e.g. logoutWithAPI) so it
  // isn't at the mercy of the current URL.
  const logout = (context?: 'admin' | 'reception') => {
    const rcp = context === 'reception' || (context === undefined && isRcpPath())
    const keys = rcp
      ? { token: RCP_TOKEN_KEY, authFlag: RCP_AUTH_FLAG_KEY, user: RCP_USER_KEY }
      : { token: 'authToken', authFlag: 'isAuthenticated', user: 'user' }

    user.value = null
    token.value = null
    isAuthenticated.value = false
    isImpersonating.value = false
    impersonator.value = null
    localStorage.removeItem(keys.authFlag)
    localStorage.removeItem(keys.user)
    localStorage.removeItem(keys.token)
    if (!rcp) localStorage.removeItem(ADMIN_TOKEN_KEY)
    if (rcp) localStorage.removeItem(RCP_FROM_SIGNUP_KEY)
    localStorage.removeItem('language')
    localStorage.removeItem('trusted_device_email')
    resetAllStores()
  }

  /**
   * Start impersonating an entity. `type` is 'user' | 'company' | 'partner',
   * `id` is the row id the Super Admin clicked "login as" on. The admin's own
   * token is stashed so it can be restored on exit without re-authenticating.
   */
  const startImpersonation = async (
    type: 'user' | 'company' | 'partner',
    id: number | string,
  ) => {
    try {
      const adminToken = token.value
      const response = await apiInstance.post('/admin/impersonate/take', { type, id })
      const data = response.data

      if (data.error === true || !data.data?.token) {
        return { success: false, message: data.message || 'Failed to start impersonation' }
      }

      // Stash the admin token and impersonation target type so they can be restored on exit.
      if (adminToken) localStorage.setItem(ADMIN_TOKEN_KEY, adminToken)
      localStorage.setItem(IMPERSONATION_TYPE_KEY, type)
      const impersonationToken = data.data.token
      token.value = impersonationToken
      localStorage.setItem('authToken', impersonationToken)

      // Reset cached store data and load the impersonated user's profile.
      resetAllStores()
      const profileResult = await fetchUserProfile(impersonationToken)
      if (profileResult.success && profileResult.userData) {
        login(profileResult.userData)
        if (profileResult.userData.defaultLang) {
          setLanguage(profileResult.userData.defaultLang)
          updateElementPlusLocale(profileResult.userData.defaultLang)
        }
        return { success: true, message: data.message || null }
      }

      // Profile failed — roll back to the admin token.
      await stopImpersonation()
      return { success: false, message: profileResult.message || 'Failed to load impersonated profile' }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Network error while starting impersonation',
      }
    }
  }

  /**
   * Stop impersonating: revoke the impersonation token server-side, then restore
   * the Super Admin's own token and reload their profile.
   */
  const stopImpersonation = async () => {
    const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY)
    const targetType = localStorage.getItem(IMPERSONATION_TYPE_KEY) as 'user' | 'company' | 'partner' | null

    // Best-effort: tell the backend to close the session + stamp the log.
    try {
      await apiInstance.post('/impersonate/leave', {})
    } catch (error: any) {
      console.error('Leave impersonation error:', error.response?.status, error.message)
    }

    isImpersonating.value = false
    impersonator.value = null
    localStorage.removeItem(ADMIN_TOKEN_KEY)
    localStorage.removeItem(IMPERSONATION_TYPE_KEY)
    resetAllStores()

    if (!adminToken) {
      // No admin token to restore — fall back to a clean logout. Impersonation is
      // always admin-context, never inferred from the current path.
      logout('admin')
      return { success: false, targetType: null, message: 'Admin session expired, please log in again.' }
    }

    token.value = adminToken
    localStorage.setItem('authToken', adminToken)

    const profileResult = await fetchUserProfile(adminToken)
    if (profileResult.success && profileResult.userData) {
      login(profileResult.userData)
      if (profileResult.userData.defaultLang) {
        setLanguage(profileResult.userData.defaultLang)
        updateElementPlusLocale(profileResult.userData.defaultLang)
      }
      return { success: true, targetType, message: null }
    }

    logout('admin')
    return { success: false, targetType: null, message: 'Admin session expired, please log in again.' }
  }

  const initializeAuth = () => {
    const savedToken = localStorage.getItem(isRcpPath() ? RCP_TOKEN_KEY : 'authToken')

    if (!savedToken) {
      isAuthenticated.value = false
      localStorage.removeItem(isRcpPath() ? RCP_AUTH_FLAG_KEY : 'isAuthenticated')
      return
    }

    token.value = savedToken
    // Always fetch the latest profile (no localStorage cache) so account changes
    // reflect immediately.
    fetchUserProfile(savedToken).then((profileResult) => {
      if (profileResult.success && profileResult.userData) {
        login(profileResult.userData)
        if (profileResult.userData.defaultLang) {
          setLanguage(profileResult.userData.defaultLang)
          updateElementPlusLocale(profileResult.userData.defaultLang)
        }
      } else if (profileResult.unauthorized) {
        logout()
      }
    })
  }

  // applyLanguage: false keeps the current UI language, for a session opened in the background
  // (signup's reception login) whose account language is not the viewer's.
  const autoLoginWithToken = async (authToken: string, { applyLanguage = true } = {}) => {
    try {
      token.value = authToken

      const profileResult = await fetchUserProfile(authToken)
      if (profileResult.success && profileResult.userData) {
        localStorage.setItem(keysFor(profileResult.userData.role).token, authToken)
        login(profileResult.userData)
        if (applyLanguage && profileResult.userData.defaultLang) {
          setLanguage(profileResult.userData.defaultLang)
          updateElementPlusLocale(profileResult.userData.defaultLang)
        }
        return {
          success: true,
          userData: profileResult.userData,
          message: null,
        }
      } else {
        logout()
        return {
          success: false,
          userData: null,
          message: profileResult.message || 'Failed to fetch user profile',
        }
      }
    } catch (error: any) {
      console.error('Auto-login error:', error.message)
      logout()
      return {
        success: false,
        userData: null,
        message: error.response?.data?.message || 'Network error. Please try again.',
      }
    }
  }

  const redeemRCPLoginToken = async (loginToken: string) => {
    try {
      const response = await apiInstance.post('/restaurant/rcp/redeem-login-token', { token: loginToken })
      const data = response.data

      if (data.error === true || !data.data?.token) {
        return {
          success: false,
          token: null,
          restaurantDomain: null,
          accountEmail: null,
          message: data.message || 'Failed to redeem login token',
        }
      }

      return {
        success: true,
        token: data.data.token as string,
        restaurantDomain: data.data.restaurant_domain as string,
        accountEmail: data.data.account_email as string,
        message: null,
      }
    } catch (error: any) {
      console.error('RCP login token redemption error:', error.response?.status, error.message)
      return {
        success: false,
        token: null,
        restaurantDomain: null,
        accountEmail: null,
        message: error.response?.data?.message || 'Network error. Please try again.',
      }
    }
  }

  const generatePUMLoginToken = async (restaurantSlug: string, userEmail: string) => {
    try {
      const response = await apiInstance.get(`/restaurant/pum/login/${restaurantSlug}/${userEmail}`)
      const data = response.data

      if (data.error === true) {
        return {
          success: false,
          token: null,
          message: data.message || 'Failed to generate login token',
        }
      }

      if (!data.data || !data.data.token) {
        return {
          success: false,
          token: null,
          message: 'Invalid response from server',
        }
      }

      const loginToken = data.data.token
      return {
        success: true,
        token: loginToken,
        message: null,
      }
    } catch (error: any) {
      console.error('PUM login token error:', error.response?.status, error.message)
      return {
        success: false,
        token: null,
        message: error.response?.data?.message || 'Network error. Please try again.',
      }
    }
  }

  const registerWithAPI = async (registrationData: {
    company: string
    first_name: string
    last_name: string
    email: string
    street: string
    zip_code: string
    city: string
    country_id: number
    password: string
    accept_terms: number
    data_protection: number
    lang: string
    restaurant_name: string
    restaurant_street: string
    restaurant_city: string
    restaurant_zip_code: string
    restaurant_country_id: number
    webpage_link: string
    product_type: 'order_only' | 'order_payment'
    partner_slug?: string
  }) => {
    try {
      const response = await apiInstance.post('/auth/customer/register', registrationData)
      const data = response.data

      if (data.error === true) {
        return {
          success: false,
          errors: data.errors || null,
          message: data.message || null,
        }
      }

      if (data.error === false) {
        return {
          success: true,
          errors: null,
          message: data.message,
        }
      }

      return {
        success: false,
        errors: null,
        message: 'An unexpected error occurred',
      }
    } catch (error: any) {
      console.error('Registration error:', error.response?.status, error.message)

      if (error.response?.status === 422 && error.response?.data?.errors) {
        return {
          success: false,
          errors: error.response.data.errors,
          message: null,
        }
      }

      return {
        success: false,
        errors: null,
        message: error.response?.data?.message || 'Network error. Please try again.',
      }
    }
  }

  const updateLanguage = async (language: string) => {
    try {
      const validLanguages = ['en', 'de']
      if (!validLanguages.includes(language)) {
        console.error('Invalid language:', language)
        return {
          success: false,
          message: 'Invalid language. Only en and de are supported.',
        }
      }

      const response = await apiInstance.put('/user/update-language', { language })
      const data = response.data

      if (data.success === false) {
        return {
          success: false,
          message: data.message || 'Failed to update language',
        }
      }

      if (user.value) {
        user.value.defaultLang = language
        localStorage.setItem('user', JSON.stringify(user.value))
      }

      setLanguage(language)
      updateElementPlusLocale(language)

      return {
        success: true,
        message: 'Language updated successfully',
      }
    } catch (error: any) {
      console.error('Update language error:', error.response?.status, error.message)
      return {
        success: false,
        message: error.response?.data?.message || 'Network error while updating language',
      }
    }
  }

  return {
    user,
    token,
    isAuthenticated,
    isImpersonating,
    impersonator,
    currentUser,
    isManagementRole,
    isDatevAccountant,
    loginWithMagicToken,
    login,
    startImpersonation,
    stopImpersonation,
    loginWithAPI,
    verifyOTP,
    resendOTP,
    registerWithAPI,
    autoLoginWithToken,
    redeemRCPLoginToken,
    generatePUMLoginToken,
    fetchUserProfile,
    forgotPassword,
    logout,
    logoutWithAPI,
    markReceptionSessionFromSignup,
    clearReceptionSignupMark,
    initializeAuth,
    updateLanguage,
  }
})
